import { useState } from 'react'
import '@/styles/login.css'

interface User {
  id: string;
  name: string;
  role: 'APPLICANT' | 'APPROVER' | 'ADMIN';
  email: string;
}

interface DemoLoginPageProps {
  onLogin: (user: User) => void;
  onForgotPassword: () => void;
}

export function DemoLoginPage({ onLogin, onForgotPassword }: DemoLoginPageProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    // デモ用の簡易認証
    // 利用可能なユーザー：
    // - admin（システム管理者）
    // - desk_officer1（窓口担当1）
    // - desk_officer2（窓口担当2）
    // - approver3（運航部長）
    // - approver4（整備部長）
    setTimeout(() => {
      if (username && password) {
        const userMap: Record<string, { id: string, name: string, role: 'ADMIN' | 'DESK_OFFICER' | 'APPROVER' }> = {
          'admin': { id: 'admin-001', name: 'システム管理者', role: 'ADMIN' },
          'desk_officer1': { id: 'desk-001', name: '窓口担当1', role: 'DESK_OFFICER' },
          'desk_officer2': { id: 'desk-002', name: '窓口担当2', role: 'DESK_OFFICER' },
          'approver3': { id: 'approver-003', name: '運航部長', role: 'APPROVER' },
          'approver4': { id: 'approver-004', name: '整備部長', role: 'APPROVER' }
        }
        
        const selectedUser = userMap[username] || { id: 'admin-001', name: username, role: 'ADMIN' }
        const user: User = {
          id: selectedUser.id,
          name: selectedUser.name,
          role: selectedUser.role as any,
          email: `${username}@workflow.local`
        }
        onLogin(user)
      }
      setIsLoading(false)
    }, 1000)
  }

  return (
    <div className="login-container">
      <div className="login-wrapper">
        {/* タイトル */}
        <div className="login-logo-section">
          <h1 className="login-title">
            Flowive
          </h1>
          <p className="login-subtitle">
            管理者ポータルへサインイン
          </p>
        </div>

        {/* ログインフォーム */}
        <div className="login-card">
          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-form-group">
              <label htmlFor="username">
                ユーザー名
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 1
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_320_675)">
                      <path fillRule="evenodd" clipRule="evenodd" d="M15 6.5C15 4.8 13.7 3.5 12 3.5C10.3 3.5 9 4.8 9 6.5C9 8.2 10.3 9.5 12 9.5C13.7 9.5 15 8.2 15 6.5ZM7.5 6.5C7.5 4 9.5 2 12 2C14.5 2 16.5 4 16.5 6.5C16.5 9 14.5 11 12 11C9.5 11 7.5 9 7.5 6.5ZM19.5 22.0001H21H21.1V20.8001C21.1 16.6001 17.6 13.1001 13.3 13.1001H10.8C6.5 13.1001 3 16.5001 3 20.8001V22.0001H4.6V20.8001C4.6 17.4001 7.4 14.6001 10.8 14.6001H13.3C16.7 14.6001 19.5 17.4001 19.5 20.8001V22.0001Z" fill="#9CA3AF"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_320_675">
                        <rect width="24" height="24" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <input
                  id="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  autoComplete="username"
                  style={{ paddingLeft: '40px' }}
                />
              </div>
            </div>

            <div className="login-form-group">
              <label htmlFor="password">
                パスワード
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 1
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7 8V6C7 3.2 9.2 1 12 1C14.8 1 17 3.2 17 6V8H19C20.1 8 21 8.9 21 10V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V10C3 8.9 3.9 8 5 8H7ZM9 6V8H15V6C15 4.3 13.7 3 12 3C10.3 3 9 4.3 9 6ZM5 10V19H19V10H5ZM12 13C11.4 13 11 13.4 11 14V16C11 16.6 11.4 17 12 17C12.6 17 13 16.6 13 16V14C13 13.4 12.6 13 12 13Z" fill="#9CA3AF"/>
                  </svg>
                </div>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  style={{ paddingLeft: '40px' }}
                />
              </div>
            </div>

            <div className="login-options">
              <label className="login-remember">
                <input
                  type="checkbox"
                />
                <span>
                  ログイン状態を保持
                </span>
              </label>
              <button
                type="button"
                onClick={onForgotPassword}
                className="login-forgot"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                パスワードを忘れた場合
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="login-button"
              style={{
                backgroundColor: '#2563eb',
                borderColor: '#2563eb'
              }}
            >
              {isLoading ? (
                <>
                  <svg className="login-spinner" style={{ width: '20px', height: '20px' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>認証中...</span>
                </>
              ) : (
                'サインイン'
              )}
            </button>
          </form>


          <div className="login-demo-notice">
            デモ環境 | 任意のユーザー名・パスワードでログイン可能
          </div>
        </div>

        {/* フッター */}
        <div className="login-footer">
          © 2025 Workflow Management System. All rights reserved.
        </div>
      </div>
    </div>
  )
}